import { pickStatisticsScopeFields } from '../../../filters/lib/statisticsFilterParams';
import { buildPersistedChartFilters, isPipelineBuiltinChartKind, PERSISTED_CHART_KIND, pickTrendFields, resolvePersistedChartKind } from './statisticsPersistedCharts';
import { attachFrozenDataSnapshot, attachLiveRefreshMode, isFrozenChartSnapshot, isLiveChartSnapshot, isPageBoundChartSnapshot } from './statisticsChartRefreshMode';
import { DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID, isBuiltinOverdueRankSeedSnapshot } from '../../overdue/lib/overdueChartConfig';

export { DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID };

export const DASHBOARD_CHART_SOURCE = {
	BUILTIN: 'builtin',
	USER: 'user',
};

export const chartUsesPageFilters = (snapshot = {}) => Boolean(snapshot?.usesPageFilters);

/** 跟页图用顶栏业务筛选 + 图自身趋势/组成配置；其余用保存时口径 */
export const resolvePersistedChartViewFilters = (snapshot, pageAppliedFilters) => {
	const stored = buildPersistedChartFilters(snapshot);
	if (!isPageBoundChartSnapshot(snapshot) || !pageAppliedFilters || typeof pageAppliedFilters !== 'object') {
		return stored;
	}
	return {
		...stored,
		...pickStatisticsScopeFields(pageAppliedFilters),
	};
};

export const resolveChartRefreshMode = (snapshot, fallback = 'frozen') => {
	if (isFrozenChartSnapshot(snapshot)) return 'frozen';
	if (isLiveChartSnapshot(snapshot) && snapshot?.refreshMode === 'live') return 'live';
	if (!snapshot?.refreshMode) return fallback;
	return snapshot.refreshMode;
};

/** 将 applied 筛选写入 snapshot 的 scope/trend，并取消跟页 */
export const applyFiltersToChartSnapshot = (snapshot = {}, appliedFilters = null) => {
	if (!appliedFilters || typeof appliedFilters !== 'object') {
		return {
			...snapshot,
			usesPageFilters: false,
		};
	}
	return {
		...snapshot,
		scope: pickStatisticsScopeFields(appliedFilters),
		trend: pickTrendFields(appliedFilters),
		usesPageFilters: false,
	};
};

/** live → frozen：烤入 filters + bundle */
export const buildFrozenFromLive = ({ snapshot, appliedFilters = null, chartBundle = null } = {}) => {
	if (!snapshot) return null;
	const withFilters = chartUsesPageFilters(snapshot) || appliedFilters ? applyFiltersToChartSnapshot(snapshot, appliedFilters ?? {}) : { ...snapshot, usesPageFilters: false };
	const frozen = attachFrozenDataSnapshot(withFilters, chartBundle);
	const { sourceStatisticsChartId: _drop, ...rest } = frozen;
	return {
		...rest,
		usesPageFilters: false,
		pinnedAt: frozen.pinnedAt ?? new Date().toISOString(),
	};
};

/** frozen → live：保留 filters，去掉快照数据；builtin 恢复跟页 */
export const buildLiveFromFrozen = (snapshot = {}) => {
	const { dataSnapshot: _ds, pinnedAt: _pa, liveDisplayFallback: _lf, ...rest } = snapshot;
	const restorePageFilters = rest.source === DASHBOARD_CHART_SOURCE.BUILTIN || Boolean(rest.templateId);
	return {
		...attachLiveRefreshMode(rest),
		usesPageFilters: restorePageFilters,
	};
};

export const isBuiltinChartSnapshot = (snapshot = {}) => snapshot?.source === DASHBOARD_CHART_SOURCE.BUILTIN || Boolean(snapshot?.templateId);

/** 经营看板默认超期排行（横向条 + 列表），含首页 pin 自 builtin 种子 */
export const isBuiltinOverdueRankChartSnapshot = (snapshot = {}) =>
	resolvePersistedChartKind(snapshot) === PERSISTED_CHART_KIND.OVERDUE && isBuiltinOverdueRankSeedSnapshot(snapshot);

/** builtin 图 live 且跟页：直接读页面 live 数据渲染，不走批量拉数（首页 pin 为 usesPageFilters:false，须走 chartBundle） */
export const rendersAsBuiltinLiveChart = (snapshot = {}) => {
	if (!isLiveChartSnapshot(snapshot)) return false;
	if (!chartUsesPageFilters(snapshot)) return false;
	return isPipelineBuiltinChartKind(snapshot?.chartKind);
};

/** live 图均可定格（含 builtin）；定格时须由调用方提供可序列化 chartBundle（页面 lens/overdue 等） */
export const canFreezeChartSnapshot = (snapshot = {}) => isLiveChartSnapshot(snapshot);

/** 任意图均可复制；复制必带 applied filters（由 duplicate API 注入） */
export const canDuplicateChartSnapshot = (_snapshot = {}) => true;

/** 从经营看板页面 live 状态组装 builtin 定格用 bundle */
export const buildBuiltinLiveFreezeBundle = ({ snapshot, statsState = null, overdueData = null } = {}) => {
	const chartKind = snapshot?.chartKind;
	if (!chartKind) return null;
	if (chartKind === PERSISTED_CHART_KIND.OVERDUE || String(chartKind).startsWith('pipeline-')) {
		return {
			chartKind,
			activeMetric: statsState?.activeMetric ?? null,
			activeDimension: statsState?.activeDimension ?? null,
			selectedPhase: statsState?.selectedPhase ?? null,
			lensData: statsState?.lensData ?? null,
			dimensionRows: statsState?.dimensionRows ?? [],
			chartAggregates: statsState?.chartAggregates ?? null,
			orders: Array.isArray(statsState?.orders) ? statsState.orders.slice(0, 500) : [],
			highlightProvinceNames: statsState?.highlightProvinceNames ?? [],
			highlightAnchorCityRows: statsState?.highlightAnchorCityRows ?? [],
			overdueData: overdueData ?? null,
			chartLoading: false,
			animationKey: `frozen-${snapshot?.id ?? 'builtin'}`,
		};
	}
	return null;
};

/** 是否为已定格的 builtin/pipeline 图（用 dataSnapshot 渲染，而非页面 live） */
export const rendersAsFrozenBuiltinChart = (snapshot = {}) =>
	isFrozenChartSnapshot(snapshot) &&
	Boolean(snapshot?.dataSnapshot) &&
	isPipelineBuiltinChartKind(snapshot?.chartKind);
